import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { useCustomErrors } from "go-form/hooks";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { partnerApi } from "../../../../services/Api/Partner/partnerApi";
import { PartnerApi, PartnerFormProps } from "../../../../services/Api/Partner/partnerTypes";

interface Props {
	partner: PartnerApi;
	customFieldsConfig: CustomFieldTemplateApi[];
}

const PartnerForm = ({ partner, customFieldsConfig }: Props) => {
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation("admin");
	const { addFlash, addSuccessFlash } = useFlash();
	const form = useForm<PartnerFormProps>({
		defaultValues: {
			company_email: partner.company_email,
			contact_person: partner.contact_person,
			codes: partner.codes,
			company_phone_number: partner.company_phone_number,
			custom_fields: mapCustomFieldsToForm(customFieldsConfig, partner.custom_fields),
		},
	});
	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors },
		setError,
		formState,
		getValues,
		setValue,
		watch,
	} = form;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	const onChangeTags = (values: string[]) => {
		setValue("codes", values);
	};

	const onCreateTag = (value: string) => {
		setValue("codes", [...getValues().codes, value], { shouldDirty: true });
	};

	const mapTags = (tags: string[]) => {
		return tags?.map((tag) => {
			return {
				value: tag,
				label: tag,
			};
		});
	};

	const onSubmit = handleSubmit(async (data) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			const params = { include: "codes,any_sub_partner,custom_fields" };
			const res = await partnerApi.updatePartner(partner.id, data, params);
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			reset({
				...res,
				custom_fields: mapCustomFieldsToForm(customFieldsConfig, res.custom_fields),
			});
		} catch (e) {
			handleError.form(e, setError, addFlash);
		}
		setLoading(false);
	});

	return (
		<FormDirty onSubmit={onSubmit} loading={loading} formState={formState}>
			<fieldset className={"form-group no-gutters"}>
				<h5>{t("common.word.basic_data", { ns: "lib" })}</h5>
				<div className={"row"}>
					<div className={"col-md-12"}>
						<FormInput
							label={t("common.word.contact_info")}
							register={register}
							name="contact_person"
							errors={errors}
						/>
						<FormInput
							label={t("modules.partner.field.company_phone_number.title")}
							register={register}
							name="company_phone_number"
							errors={errors}
						/>
						<FormInput
							label={t("modules.partner.field.company_email.title")}
							register={register}
							name="company_email"
							errors={errors}
						/>
						<FormSelectGroup
							control={control}
							onCreateOption={onCreateTag}
							isMulti
							onChange={onChangeTags}
							loadOptions={() => mapTags(watch("codes"))}
							defaultValue={mapTags(watch("codes"))}
							errors={errors}
							name="codes"
							label={t("modules.partner.field.codes.title")}
							data-testid="codes"
						/>
					</div>
				</div>
			</fieldset>
			<fieldset className="form-group no-gutters">
				<h5>{t("common.word.advanced", { ns: "lib" })}</h5>
				<CustomFieldsForm
					form={form}
					customFieldsConfig={customFieldsConfig}
					customFields={partner.custom_fields}
					setErrors={setErrors}
				/>
			</fieldset>
		</FormDirty>
	);
};
export default PartnerForm;
